import React, { useEffect, useRef, useState } from 'react';
import { FileText, Copy, Check, X } from 'lucide-react';

export const EXAMPLE_DOCUMENTS = [
  {
    id: 'tender-disability-confident',
    type: 'Tender Response',
    title: 'Disability Confident Commitment — Social Value Response',
    summary: 'A model answer to a typical social-value tender question on disability inclusion.',
    content: `## Disability Confident Commitment

**Question:** Describe your organisation's commitment to the recruitment, retention and development of disabled people, and how this will be delivered under this contract.

### Our Commitment
[Organisation Name] is a Disability Confident Committed employer and is actively progressing toward Disability Confident Leader (Level 3) status. Our commitment is embedded in our People Strategy and underpinned by our Accessibility & Inclusion Policy.

### What We Have in Place
- **Guaranteed Interview Scheme** — all disabled applicants who meet the minimum criteria are guaranteed an interview.
- **Reasonable Adjustments Process** — a documented, end-to-end process covering recruitment, onboarding and ongoing employment, with adjustment requests actioned within 5 working days.
- **Inclusive Recruitment** — job adverts use inclusive language, are published on disability-focused job boards, and the application journey has been accessibility-tested with disabled users.
- **Workplace Adjustments Policy** — supporting employees who acquire a disability or whose condition changes, including phased returns and flexible working.
- **Leadership Accountability** — disability inclusion metrics are reported to the Board quarterly.

### Delivery Under This Contract
We will apply these standards to all roles created or filled under this contract, and will:
1. Offer guaranteed interviews to disabled candidates meeting the essential criteria.
2. Provide accessible recruitment materials and adjustment support at every stage.
3. Report annually on disability employment outcomes as part of our social value delivery.

### Evidence Available
Our Disability Confident self-assessment, inclusive recruitment policy, and reasonable adjustments framework are available on request.`,
  },
  {
    id: 'esg-social-inclusion',
    type: 'ESG Report',
    title: 'ESG Report — Social: Disability Inclusion',
    summary: 'A board-ready ESG narrative section on disability inclusion for annual reporting.',
    content: `## Social — Disability Inclusion

### Approach
Inclusion of disabled people is a strategic priority for [Organisation Name]. We measure our progress against the Disability Confident scheme and are working toward Level 3 (Leader) accreditation. Our approach spans recruitment, retention, workplace adjustment, and leadership accountability.

### Performance Highlights
- **Disability Confident status:** Level [X] — [Committed / Employer / Leader]
- **Maturity score:** [XX]% (weighted across Levels 1–3)
- **Guaranteed interviews offered:** [N] disabled candidates meeting minimum criteria in the reporting period
- **Reasonable adjustments fulfilled:** [N] requests, [XX]% within 5 working days
- **Workforce disclosure rate:** [XX]% of employees have shared disability status

### Key Activities
- Reviewed all job adverts and application processes for inclusive language and accessibility.
- Launched a workplace adjustments policy with a single, tracked request route.
- Trained [N] managers in disability awareness and inclusive recruitment.
- Established an employee disability network with executive sponsorship.

### Forward Priorities
- Achieve Disability Confident Leader (Level 3) status via independent external validation.
- Publish disability workforce data and progression outcomes annually.
- Embed disability metrics in leadership performance objectives.

### Governance
Disability inclusion progress is reported to the Board [quarterly] and reviewed by the [People Committee].`,
  },
  {
    id: 'board-pack-inclusion',
    type: 'Board Pack Insert',
    title: 'Board Pack Insert — Disability Inclusion Update',
    summary: 'A one-page board pack insert summarising disability inclusion progress and asks.',
    content: `# Disability Inclusion — Board Update

**Prepared for:** Board / People Committee
**Period:** [Quarter / Year]
**Lead:** [Name, Role]

## Headline
Disability Confident maturity now at **Level [X] ([XX]% maturity score)**, up from Level [Y] at last review. On track for Level 3 validation in [target date].

## Progress This Period
| Measure | Status |
|---|---|
| Inclusive recruitment policy adopted | ✅ Complete |
| Guaranteed interview scheme live | ✅ Complete |
| Reasonable adjustments process | ✅ Complete |
| Manager disability awareness training | 🟡 [XX]% complete |
| Level 2 self-assessment | 🟡 In progress |
| External validation (Level 3) | ⏳ Planned [date] |

## Risks
- **Credibility risk:** Level 1 badge held with limited Level 2 evidence — mitigated by completing self-assessment this quarter.
- **Data gap:** Workforce disability disclosure rate at [XX]% — targeted disclosure campaign required.

## Decisions Requested
1. Approve progression to Disability Confident Leader (Level 3) external validation.
2. Endorse quarterly disability metrics reporting to the Board.
3. Approve budget for manager training rollout ([£X]).

## Next Milestones
- [Date] — Level 2 self-assessment submitted
- [Date] — External validation evidence uploaded
- [Date] — Level 3 accreditation decision`,
  },
];

export default function ExampleDocuments() {
  const [openId, setOpenId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState(null);
  const mounted = useRef(true);
  const openIdRef = useRef(null);
  const copyAction = useRef(0);
  const copyTimer = useRef(null);
  openIdRef.current = openId;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      copyAction.current += 1;
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, []);

  function clearCopyFeedback() {
    copyAction.current += 1;
    setCopying(false);
    setCopied(false);
    setCopyError(null);
    if (copyTimer.current) {
      clearTimeout(copyTimer.current);
      copyTimer.current = null;
    }
  }

  async function copy(doc) {
    const action = ++copyAction.current;
    const documentId = doc.id;
    setCopying(true);
    setCopied(false);
    setCopyError(null);
    try {
      await navigator.clipboard.writeText(doc.content);
      if (
        !mounted.current ||
        copyAction.current !== action ||
        openIdRef.current !== documentId
      )
        return;
      setCopying(false);
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => {
        if (
          mounted.current &&
          copyAction.current === action &&
          openIdRef.current === documentId
        ) {
          setCopied(false);
          copyTimer.current = null;
        }
      }, 1500);
    } catch {
      if (
        mounted.current &&
        copyAction.current === action &&
        openIdRef.current === documentId
      ) {
        setCopying(false);
        setCopyError('Unable to copy this example document.');
      }
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Ready-to-use example documents you can adapt for tenders, ESG reports and board packs.
      </p>
      <div className="grid grid-cols-1 gap-3">
        {EXAMPLE_DOCUMENTS.map((doc) => {
          const open = openId === doc.id;
          return (
            <div key={doc.id} className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  clearCopyFeedback();
                  setOpenId(open ? null : doc.id);
                }}
                aria-expanded={open}
                className="flex items-start gap-3 w-full p-4 text-left hover:bg-muted/50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
              >
                <FileText className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-teal-100 text-teal-700">{doc.type}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground mt-1">{doc.title}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{doc.summary}</p>
                </div>
              </button>
              {open && (
                <div className="border-t border-border px-4 py-4 bg-muted/30">
                  <div className="flex justify-end mb-2">
                    <button
                      type="button"
                      onClick={() => copy(doc)}
                      disabled={copying}
                      className="flex min-h-11 items-center gap-1.5 px-2 text-xs font-medium text-teal-700 hover:text-teal-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring rounded"
                    >
                      {copied ? <Check className="w-3.5 h-3.5" aria-hidden="true" /> : <Copy className="w-3.5 h-3.5" aria-hidden="true" />}
                      {copied ? 'Copied' : copying ? 'Copying…' : 'Copy'}
                    </button>
                  </div>
                  {copyError && <p role="alert" className="mb-2 text-sm text-red-700">{copyError}</p>}
                  <pre tabIndex={0} className="text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed max-h-96 overflow-y-auto">{doc.content}</pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
